import React, { useState, useEffect, useRef } from 'react';
import { Eye, ArrowRight, Search, Brain, TrendingUp, ShieldCheck, BarChart2, Target, AlertCircle } from 'lucide-react';

const ThreeEye = () => {
  // Stage 0: Only Insight, Stage 1: Intelligence + Insight preview, 
  // Stage 2: Implementation + both previews
  const [activeStage, setActiveStage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // Check device size on mount and window resize
  useEffect(() => {
    const checkDeviceSize = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024); // md breakpoint
    };

    // Initial check
    checkDeviceSize();

    // Add resize listener
    window.addEventListener('resize', checkDeviceSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkDeviceSize);
  }, []);
  
  // Auto rotate through the stages - only on desktop
  useEffect(() => {
    if (isMobile || isTablet) return; // Skip animation for mobile/tablet

    const threeLensElement = document.getElementById('three-lens');
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Start animation when in viewport (desktop only)
          timerRef.current = setInterval(() => {
            if (!isPaused) {
              setActiveStage((prev) => {
                if (prev === 2) {
                  if (timerRef.current) {
                    clearInterval(timerRef.current);
                  }
                  return 2;
                }
                return prev + 1;
              });
            }
          }, 1500);
        } else {
          // Pause animation when out of viewport
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
        }
      },
      { threshold: 0.5 } // Trigger when 50% of element is visible
    );

    if (threeLensElement) {
      observer.observe(threeLensElement);
    }

    // Clean up
    return () => {
      if (threeLensElement) {
        observer.unobserve(threeLensElement);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPaused, isMobile, isTablet]);
  
  // Handle stage click - allow direct navigation to any stage
  const handleStageClick = (index) => {
    setActiveStage(index);
    
    if (!isMobile && !isTablet) {
      setIsPaused(true);
      
      // Resume auto-rotation after 10 seconds of inactivity (desktop only)
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      
      timerRef.current = setTimeout(() => {
        setIsPaused(false);
      }, 1000);
    }
  };

  // Frameworks data for the three eyes
  const eyeFrameworks = [
    {
      title: "Insight",
      icon: Search,
      color: "#1C52A0",
      description: "Deep analysis of fund performance and potential",
      elements: [
        {
          icon: BarChart2,
          title: "Performance Analysis",
          description: "Historic returns analysis"
        },
        {
          icon: Brain,
          title: "Fund Manager Assessment",
          description: "Track record"
        },
        {
          icon: Target,
          title: "Investment Objective",
          description: "Goal alignment"
        }
      ]
    },
    {
      title: "Intelligence",
      icon: Brain,
      color: "#113262",
      description: "Smart monitoring and adaptation",
      elements: [
        {
          icon: Eye,
          title: "Continuous Monitoring",
          description: "Regular performance and risk tracking"
        },
        {
          icon: TrendingUp,
          title: "Market Intelligence",
          description: "Industry trends and market dynamics"
        },
        {
          icon: Target,
          title: "Strategic Adjustments",
          description: "Dynamic portfolio rebalancing"
        }
      ]
    },
    {
      title: "Implementation",
      icon: TrendingUp,
      color: "#F49611",
      description: "Strategic execution and portfolio integration",
      elements: [
        {
          icon: ShieldCheck,
          title: "Risk Management",
          description: "Portfolio risk assessment and mitigation"
        },
        {
          icon: AlertCircle,
          title: "Compliance Check",
          description: "Regulatory and mandate compliance"
        },
        {
          icon: ArrowRight,
          title: "Execution Strategy",
          description: "Optimal entry and allocation planning"
        }
      ]
    }
  ];

  // Render mobile/tablet version
  if (isMobile || isTablet) {
    return (
      <div id="three-lens" className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
        {/* Header */}
        <div className="text-center mb-6 pt-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#113262] mb-2">
            Three-Lens Framework
          </h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
            Our comprehensive approach to mutual fund selection and monitoring
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center border-b border-gray-200 mb-6">
          {eyeFrameworks.map((framework, index) => (
            <button
              key={framework.title}
              onClick={() => handleStageClick(index)}
              className={`flex items-center px-3 py-2 text-sm sm:text-base border-b-2 transition-colors duration-300 ${
                activeStage === index 
                  ? `border-${framework.color.replace('#', '')} text-[#113262] font-medium` 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              style={{
                borderBottomColor: activeStage === index ? framework.color : 'transparent'
              }}
            >
              <framework.icon className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" style={{ color: framework.color }} />
              <span>{framework.title}</span>
            </button>
          ))}
        </div>

        {/* Active Framework Content */}
        <div className="mt-6">
          {/* Show only the active framework elements */}
          <div className="space-y-4 max-w-xs mx-auto">
            {eyeFrameworks[activeStage].elements.map((element, idx) => (
              <div
                key={idx}
                className="bg-white rounded-lg p-4 shadow-md border border-gray-100"
              >
                <div className="flex items-start">
                  <element.icon 
                    className="w-5 h-5 sm:w-6 sm:h-6 mr-3 flex-shrink-0" 
                    style={{ color: eyeFrameworks[activeStage].color }} 
                  />
                  <div>
                    <h4 className="text-sm sm:text-base font-semibold text-[#113262] mb-1">
                      {element.title}
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600">
                      {element.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* CTA Button - positioned at bottom right */}
        <div className="mt-8 flex justify-end">
          <a 
            href="/three-lens" 
            className="flex items-center text-[#1C52A0] hover:text-[#0A1E3A] transition-colors duration-300"
          >
            <span className="font-semibold text-sm sm:text-base">Learn More</span>
            <ArrowRight className="ml-1 h-3 w-3 sm:h-4 sm:w-4" />
          </a>
        </div>
      </div>
    );
  }

  // Desktop version (unchanged)
  return (
    <div id="three-lens" className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8 relative overflow-hidden">
      {/* Header */}
      <div className="relative text-center mb-6 pt-12">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4">
          Three-Lens Framework
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Our comprehensive approach to mutual fund selection and monitoring
        </p>
      </div>

      {/* Stage Selection Indicators */}
      <div className="flex justify-center gap-6 sm:gap-8 mb-6">
        {/* Individual framework indicators */}
        {eyeFrameworks.map((framework, index) => (
          <div
            key={framework.title}
            className={`relative cursor-pointer transition-all duration-700 ease-in-out transform hover:scale-105
              ${
                // Active if current stage is showing this framework
                (activeStage === 0 && index === 0) || // Stage 0: Only Insight
                (activeStage === 1 && index === 1) || // Stage 1: Intelligence active
                (activeStage === 2 && index === 2) // Stage 2: Implementation active
                  ? 'opacity-100'
                  : 'opacity-30 hover:opacity-50'
              }`}
            onClick={() => {
              // Map framework index to corresponding stage
              // Framework 0 (Insight) → Stage 0
              // Framework 1 (Intelligence) → Stage 1
              // Framework 2 (Implementation) → Stage 2
              handleStageClick(index);
            }}
          >
            <div className="relative">
              {/* Outer Ring */}
              <div
                className={`w-16 h-16 md:w-20 md:h-20 rounded-full transition-all duration-700 ease-in-out
                  ${
                    (activeStage === 0 && index === 0) ||
                    (activeStage === 1 && index === 1) ||
                    (activeStage === 2 && index === 2)
                      ? 'opacity-100'
                      : 'opacity-30'
                  }`}
                style={{
                  background: `conic-gradient(${framework.color} 0deg, transparent 60deg)`
                }}
              />
              
              {/* Inner Circle */}
              <div
                className={`absolute inset-2 rounded-full flex items-center justify-center
                  transition-all duration-700 ease-in-out backdrop-blur-sm
                  ${
                    (activeStage === 0 && index === 0) ||
                    (activeStage === 1 && index === 1) ||
                    (activeStage === 2 && index === 2)
                      ? 'bg-white'
                      : 'bg-white/80'
                  }`}
              >
                <framework.icon
                  className="w-7 h-7"
                  style={{ color: framework.color }}
                />
              </div>

              {/* Title */}
              <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                <span className={`text-sm font-semibold transition-colors duration-300
                  ${
                    (activeStage === 0 && index === 0) ||
                    (activeStage === 1 && index === 1) ||
                    (activeStage === 2 && index === 2)
                      ? 'text-[#113262]'
                      : 'text-gray-500'
                  }`}>
                  {framework.title}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Display Area */}
      <div className="relative mx-auto" style={{ minHeight: '250px' }}>
        {/* Stages 0-2: Progressive reveal stages */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 opacity-100 transition-opacity duration-1000">
          {eyeFrameworks.map((framework, index) => {
            // Configuration for each card based on current stage and framework index
            let position, opacity, scale, zIndex;
            
            if (activeStage === 0) {
              // Stage 0: Only show Insight
              if (index === 0) {
                // Insight Card - Centered
                position = 'translate(-50%, 0)';
                opacity = 1;
                scale = 1;
                zIndex = 30;
              } else {
                // Hide other cards
                position = 'translate(-50%, 0)';
                opacity = 0;
                scale = 1;
                zIndex = 10;
              }
            } else if (activeStage === 1) {
              // Stage 1: Intelligence active, Insight preview
              if (index === 1) {
                // Intelligence Card - Centered
                position = 'translate(0%, 0)';
                opacity = 1;
                scale = 1;
                zIndex = 30;
              } else if (index === 0) {
                // Insight Card - Preview
                position = 'translate(-100%, 0)';
                opacity = 1;
                scale = 1;
                zIndex = 30;
              } else {
                // Hide Implementation card
                position = 'translate(-50%, 0)';
                opacity = 0;
                scale = 1;
                zIndex = 10;
              }
            } else if (activeStage === 2) {
              // Stage 2: Implementation active, both Insight and Intelligence previews
              if (index === 2) {
                // Implementation Card - Right position with moderate offset
                position = 'translate(50%, 0)';
                opacity = 1;
                scale = 1;
                zIndex = 30;
              } else if (index === 0) {
                // Insight Card - Left position with moderate offset
                position = 'translate(-150%, 0)';
                opacity = 1;
                scale = 1;
                zIndex = 30;
              } else if (index === 1) {
                // Intelligence Card - Centered
                position = 'translate(-50%, 0)';
                opacity = 1;
                scale = 1;
                zIndex = 30;
              }
            }
            
            // Don't render cards that shouldn't be visible based on stage
            if (opacity === 0) return null;
            
            return (
              <div
                key={framework.title}
                className="absolute top-0 left-1/2 w-full max-w-sm transition-all duration-1000 cursor-pointer p-6"
                style={{
                  transform: `${position} scale(${scale})`,
                  opacity,
                  zIndex
                }}
                onClick={() => handleStageClick(Math.min(index + 1, 2))}
              >
                <div className="flex flex-col space-y-4 items-center min-h-[200px]">
                  {/* Framework Elements */}
                  <div className="space-y-4 w-full">
                    {framework.elements.map((element, idx) => (
                      <div
                        key={idx}
                        className="bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-lg w-full
                          transition-all duration-2000"
                        style={{ 
                          opacity: index === (activeStage === 0 ? 0 : (activeStage === 1 ? 1 : 2)) ? 1 : 1
                        }}
                      >
                        <div className="flex items-start">
                          <element.icon className="w-6 h-6 mr-3 flex-shrink-0" style={{ color: framework.color }} />
                          <div>
                            <h4 className="text-base font-semibold text-[#113262] mb-1">
                              {element.title}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {element.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* CTA Button */}
      <div className="mt-24 flex justify-end">
        <a href="/three-lens" className="flex items-center text-[#1C52A0] hover:text-[#0A1E3A] transition-colors duration-300">
          <span className="font-semibold">Learn More</span>
          <ArrowRight className="ml-2 h-4 w-4" />
        </a>
      </div>
    </div>
  );
};

export default ThreeEye;